import { mapMutations } from 'vuex';
import { parse } from 'date-fns';
import cloneDeep from 'lodash.clonedeep';
import { FormField, FormInput, FormLabel, SectionSeparatorWrapper, ApiLoading } from '@/components';
import { VUEX_BENEFIT_QUOTES_CLAIMS } from '@/constants/VuexConstants';
import { formatAsCurrency, getAddressLocalityText, getProviderDisplayName, isEmptyString, preventInvalidCurrencyInput, titleCase, isDateInRange } from '@/utils';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import ClaimsServices from '@/services/Claims';
import MultiSelect from '@general/js/components/multiSelect.vue';
import GABQnMANCMixins from './GABQnMANCMixins';

export default {
  mixins: [GABQnMANCMixins],

  components: {
    ApiLoading,
    FormField,
    FormInput,
    FormLabel,
    SectionSeparatorWrapper,
    MultiSelect,
  },

  data() {
    return {
      callApi: true,
      isActive: false, // this is required to prevent accidental update of current step form response with othe steps' form response
    };
  },

  watch: {
    'apiStatus.fail': {
      handler(data) {
        this.$emit('apiFailUpdated', data);
      },
    },

    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('responseUpdated', {
              response: this.formResponse,
              isInvalid: newData.$invalid || newData.$error,
            });
          });
        }
      },
      deep: true,
    },

    currentPage(newData, oldData) {
      // prevent calling api when navigate to previous step except when target step is 1
      if (newData > this.pages.NEW_BENEFIT_QUOTE.pageNumber) {
        this.callApi = newData > oldData;
      } else {
        this.callApi = true;
      }
    },
  },

  async activated() {
    await this.fetchStepData();
    this.isActive = true;
    this.$nextTick(() => {
      this.formResponse = this.feature[`step${this.currentPage}`];
    });
  },

  deactivated() {
    this.isActive = false;
  },

  async beforeMount() {
    const stepKey = `step${this.currentPage}`;
    const existingData = this.feature[stepKey];

    // only if not step 1
    if (this.currentPage !== this.pages.NEW_BENEFIT_QUOTE.pageNumber) {
      // overwrite form response if there are exiting one
      this.formResponse = { ...this.formResponse, ...existingData };

      const objToUpdateFeature = { ...this.feature };
      objToUpdateFeature[stepKey] = this.formResponse;

      this.updateFormResponse(objToUpdateFeature);
    }
  },

  computed: {
    selectedMemberAccountDetails() {
      let selectedMemberAccount;
      if (this.refData.accounts) {
        selectedMemberAccount = this.refData.accounts.find((obj) => {
          const step1PageNum = this.pages.NEW_BENEFIT_QUOTE.pageNumber;
          return obj.personCode === this.feature[`step${this.currentPage === step1PageNum ? step1PageNum : 5}`].member;
        });
      }
      const selectedMember = this.refData.members.find((obj) => {
        return obj.personCode === this.feature.step5.member;
      });

      const isClaimsAccountExist =
        selectedMemberAccount !== undefined &&
        selectedMemberAccount.claimsAccount !== null &&
        typeof selectedMemberAccount.claimsAccount === 'object' &&
        selectedMemberAccount.claimsAccount &&
        selectedMemberAccount.claimsAccount.accountId &&
        selectedMemberAccount.claimsAccount.bsb &&
        selectedMemberAccount.claimsAccount.accountNumber;

      const accessingSelf = (this.currentMemberDetail?.personCode === selectedMember?.personCode);
      // if the member is marked as private and the selected member is not the logged in member, mask the account details
      if (selectedMemberAccount?.keepPrivate && !accessingSelf) {
        return {
          accountId: selectedMemberAccount.claimsAccount.accountId,
          bsb: 'XXX - XXX',
          accountNumber: 'XXXX XXXX',
          accountHolderName: `${titleCase(selectedMember?.firstName)} ${titleCase(selectedMember?.lastName)}`,
        };
      }

      // Only format the account details if claims account exist
      // This includes keep private members viewing their own account details, where EAI will return the full account details based on DOA
      if (isClaimsAccountExist && ((selectedMemberAccount.keepPrivate && accessingSelf) || !selectedMemberAccount.keepPrivate)) {
        // Expect `claimsAccount` will always have `bsb` and `accountNumber`
        const accountObj = { ...selectedMemberAccount.claimsAccount };
        const bsbNoFormat = accountObj.bsb.replace(/[^0-9]/g, '');
        const accNoFormat = accountObj.accountNumber.replace(/[^0-9]/g, '');

        // format bsb and account number for display
        /**
         * for bsb, display format would be `XXX - YYYY` where first part of `XXX` will always 3 digits and whatever after ` - `
         * will show rest of the digits.
         */
        accountObj.bsb = `${bsbNoFormat.slice(0, 3)} - ${bsbNoFormat.slice(3)}`;
        /**
         * for account number, display format would be `XXXX YYYYY` where `XXXX` is first four digits and whatever after the space
         * will show rest of the digits.
         */
        accountObj.accountNumber = `${accNoFormat.slice(0, 4)} ${accNoFormat.slice(4)}`;
        return accountObj;
      }

      return null;
    },
  },

  methods: {
    ...mapMutations('BenefitQuotesClaims', {
      resetFormResponses: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.resetFormResponses,
      updateRefMemberNumber: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefMemberNumber,
      updateRefMembers: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefMembers,
      updateRefProducts: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefProducts,
      updateRefProviders: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefProviders,
      updateRefServices: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefServices,
      updateRefAccounts: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefAccounts,
    }),

    modalities(selectedMember = null, serviceDate = null) {
      if (this.feature.step1) {
        // eslint-disable-next-line arrow-body-style
        const selectedPerson = this.refData.members.find((member) => member.personCode === selectedMember);
        // combine modalities from each products and sort them in alphabetical order
        let modalitiesList = [];
        let products = this.refData.products.filter((productItem) => {
          // filter out for product object that only contains `modalities` field
          return productItem.modalities && productItem.modalities.length > 0;
        });
        let validProducts = cloneDeep(selectedPerson?.products);
        const regex = /\d{2} \/ \d{2} \/ \d{4}/;
        if (serviceDate && regex.test(serviceDate) && validProducts) {
          const today = new Date(new Date().toDateString());
          const tenYearslaterToday = new Date(today.getFullYear() + 10, today.getMonth(), today.getDate());
          validProducts = validProducts.filter((product) => {
            const startDate = parse(product.startDate, 'yyyy-MM-dd', new Date());
            const endDate = product.endDate ? parse(product.endDate, 'yyyy-MM-dd', new Date()) : tenYearslaterToday;
            return isDateInRange(serviceDate, startDate, endDate);
          });
        }

        if (selectedPerson) {
          // eslint-disable-next-line arrow-body-style
          products = products.filter((product) => validProducts.some((validProduct) => validProduct.productCode === product.productCode));
        } else {
          products = [];
        }

        products
          .map((productItem) => {
            productItem.modalities.forEach((modality) => {
              modality.productCode = productItem.productCode;
            });

            return productItem.modalities;
          })
          .forEach((modalities) => {
            modalitiesList = modalitiesList.concat(modalities);
          });

        modalitiesList.sort((a, b) => {
          if (a.modalityName > b.modalityName) return 1;
          if (a.modalityName < b.modalityName) return -1;
          return 0;
        });

        return modalitiesList.map((modality) => {
          return {
            display: modality.modalityName,
            returnValue: modality.modalityCode,
            productCode: modality.productCode,
          };
        });
      }

      return [];
    },

    /**
     * Fetch required API data for the given page.
     *
     * NOTE: Not fetching any data ahead of the required step otherwise if the user requests a page refresh, then no data may be returned for the given page.
     */
    async fetchStepData() {
      if (this.callApi) {
        // Display the loading screen
        this.apiStatus.loading = true;
        this.apiStatus.fail = false;

        const service = !this.isClaim ? BenefitQuotesServices : ClaimsServices;
        try {
          switch (this.currentPage) {
            case this.pages.NEW_BENEFIT_QUOTE.pageNumber: {
              const policyNumber = this.isCRM ? this.CRM_POLICY_NUMBER : this.policyNumber;
              const data = await service.getMembersProductsAndModalities(policyNumber);
              // Save the reference data into the cache
              if (!this.isClaim) {
                this.updateRefMemberNumber(data.memberNumber);
                this.updateRefMembers(data.members);
                this.updateRefProducts(data.products);
                if (this.isCRM && isEmptyString(this.feature.step1.member)) {
                  // Select first member in the dropdown by default
                  this.updateFormResponse({ ...this.feature, step1: { ...this.feature.step1, member: data.members[0].personCode } });
                }
              } else {
                this.updateRefMemberNumber(data.claimsMemberModalities.memberNumber);
                this.updateRefMembers(data.claimsMemberModalities.members);
                this.updateRefProducts(data.claimsMemberModalities.products);
                this.updateRefAccounts(data.claimsAccount.accounts);
              }
              break;
            }
            case this.pages.PROVIDER_SEARCH_RESULTS.pageNumber: {
              const searchType = this.feature.step2.searchType;
              const isSearchTypeNumber = searchType === 'number';
              // keeps the selected provider that user selected in step 3
              this.updateFormResponse({ ...this.feature, step3: { selectedProvider: this.getSelectedProviderId(this.formResponse.selectedProvider) } });
              this.updateStepInvalidStatus({ ...this.stepsInvalidStatus, step3: true });

              const data = await service.getProvidersForSelectedModality(
                this.feature.step1.modality,
                isSearchTypeNumber ? this.feature.step2.providerNumber : '',
                !isSearchTypeNumber ? this.feature.step2.providerSurname : '',
                !isSearchTypeNumber ? this.feature.step2.postcode : '',
                !isSearchTypeNumber ? this.feature.step2.state : '',
              );
              // Save the reference data into the cache
              this.updateRefProviders(data.providers);
              break;
            }
            case this.pages.SERVICE_INFORMATION.pageNumber: {
              const data = await service.getServicesForSelectedModality(this.feature.step1.modality);
              // Save the reference data into the cache
              this.updateRefServices(data.services);
              break;
            }
            default: {
              // Do nothing
            }
          }
        } catch (error) {
          this.apiStatus.fail = true;
          if (error.message) {
            logger.error(error.message);
          } else {
            logger.error(error);
          }
        } finally {
          // Remove the loading screen
          this.apiStatus.loading = false;
        }
      }
    },

    /**
     * Get the service's display name for the service at the given index.
     *
     * Do not use this method for calculating the service dropdown dislay name. Use `getServiceDropdownDisplayValue` instead.
     */
    getServiceDisplayName(service) {
      switch (typeof service) {
        case 'object': {
          return service.serviceId !== undefined && service.serviceName !== undefined ? `${service.serviceId} - ${service.serviceName}` : '';
        }
        case 'string': {
          const foundService = this.findServiceWithID(service);
          return foundService !== undefined ? `${foundService.serviceId} - ${foundService.serviceName}` : '';
        }
        default: {
          return '';
        }
      }
    },

    /**
     * Finds a service in the Services array with the given unique ID, or undefined if there is no match.
     */
    findServiceWithID(uniqueServiceId) {
      if (this.isEmptyString(uniqueServiceId)) {
        return undefined;
      }

      const serviceIdComponents = this.getUniqueServiceIdComponents(uniqueServiceId);
      return this.refData.services.find((service) => {
        return service.serviceId === serviceIdComponents.id && service.flexCode === serviceIdComponents.flexCode;
      });
    },

    /**
     * Convert the given `uniqueServiceId` string back into its individual identifier components.
     */
    getUniqueServiceIdComponents(uniqueServiceId) {
      const idComponents = uniqueServiceId.split('-');
      return { id: idComponents[0], flexCode: idComponents[1] };
    },

    /**
     * Determines the display label for the Service dropdown.
     */
    getServiceDropdownDisplayValue(uniqueServiceId) {
      const serviceDisplayName = this.getServiceDisplayName(uniqueServiceId);
      return !this.isEmptyString(serviceDisplayName) ? serviceDisplayName : 'Enter/select the item number or description';
    },

    /**
     * Function to determine if Tooth ID is required for the service with the given unique ID.
     */
    isToothIdRequired(serviceId) {
      const service = this.refData.services.find((obj) => {
        return obj.serviceId === serviceId;
      });
      return service !== undefined ? service.toothIdRequired : false;
    },

    /**
     * Function to determine if Current Provider selected previously is still in the list of api providers.
     */
    getSelectedProviderId(currentProviderId) {
      for (let i = 0; i < this.refData.providers.length; i++) {
        if (currentProviderId === this.refData.providers[i].providerId) {
          return currentProviderId;
        }
      }
      return '';
    },

    formatAsCurrency,
    getAddressLocalityText,
    getProviderDisplayName,
    isEmptyString,
    preventInvalidCurrencyInput,
    titleCase,
  },
};
